import { Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface DeleteItemsConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  count: number;
  itemTitle?: string;
  isDeleting?: boolean;
  onConfirm: () => void;
}

export function DeleteItemsConfirmDialog({
  open,
  onOpenChange,
  count,
  itemTitle,
  isDeleting = false,
  onConfirm,
}: DeleteItemsConfirmDialogProps) {
  const isMulti = count > 1;
  const noun = isMulti ? 'Items' : 'Item';
  const subject = isMulti ? 'Items' : 'The item';

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="!px-5 !py-5 !gap-3 max-w-[420px]">
        <AlertDialogHeader className="!space-y-1.5">
          <AlertDialogTitle className="sr-only">Delete {noun}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-1.5">
              <p className="text-[15.5px] leading-snug">
                {isMulti ? (
                  <>Are you sure you want to delete these <strong>{count} items</strong>?</>
                ) : itemTitle ? (
                  <>Are you sure you want to delete <strong>"{itemTitle}"</strong>?</>
                ) : (
                  <>Are you sure you want to delete this item?</>
                )}
              </p>
              <p className="text-xs text-muted-foreground leading-snug">
                This action cannot be undone. {subject} will be permanently removed from your inventory.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="!flex-col gap-2 sm:!flex-row sm:!justify-end sm:gap-2">
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isDeleting}
            className="!bg-none !bg-destructive !text-destructive-foreground hover:!bg-destructive/90 !shadow-none border-0 !mt-0 order-1 sm:order-2 h-11 w-full sm:w-auto"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            {isDeleting ? 'Deleting...' : isMulti ? `Delete ${count} Items` : 'Delete'}
          </AlertDialogAction>
          <AlertDialogCancel
            disabled={isDeleting}
            className="!mt-0 order-2 sm:order-1 h-11 w-full sm:w-auto"
          >
            Cancel
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
